import React from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ShieldCheck, Check, X } from 'lucide-react';

export interface VerificationRequest {
  id: string;
  userId: string;
  displayName: string;
  role: string;
  documentType: string;
  documentImage: string;
  selfieImage: string;
  submittedAt: Date;
  status: string;
}

interface VerificationRequestsProps {
  requests: VerificationRequest[];
  onVerify: (requestId: string, status: 'approved' | 'rejected') => void;
}

const VerificationRequests: React.FC<VerificationRequestsProps> = ({ requests, onVerify }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Verification Requests</CardTitle>
        <CardDescription>
          Review and approve verification requests from escorts and clients.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <ShieldCheck size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Pending Verifications</h3>
            <p className="text-muted-foreground">
              There are currently no verification requests to review.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Document</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={`https://via.placeholder.com/32?text=${request.displayName.charAt(0)}`} />
                        <AvatarFallback>{request.displayName.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{request.displayName}</div>
                        <Badge variant="outline" className={
                          request.role === 'escort' ? 'border-pink-DEFAULT text-pink-DEFAULT' :
                          'border-blue-500 text-blue-500'
                        }>
                          {request.role.charAt(0).toUpperCase() + request.role.slice(1)}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    Identity Verification
                  </TableCell>
                  <TableCell>
                    {request.documentType}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {new Date(request.submittedAt).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(request.submittedAt).toLocaleTimeString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      request.status === 'pending' ? 'bg-amber-500' :
                      request.status === 'approved' ? 'bg-green-500' :
                      'bg-red-500'
                    }>
                      {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                      >
                        View Documents
                      </Button>
                      <Button 
                        variant="default" 
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => onVerify(request.id, 'approved')}
                        disabled={request.status !== 'pending'}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => onVerify(request.id, 'rejected')}
                        disabled={request.status !== 'pending'}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default VerificationRequests;
